// Date Util

import _ from 'lodash'
import jstz from 'jstz'
import moment from 'moment'
import IntlUtil from './intlutil.jsx'
import humanizeDuration from 'humanize-duration'

class DateUtil {
  constructor () {
    this._timezone = ''
    this._offset = 0
    this._isDutchFormat = false
    this._isUsFormat = false
  }

  setTimezone (timezone) {
    const info = this.getTimezoneInfo(timezone)
    if (info) {
      this._timezone = timezone
      this._offset = info.offset
    }
  }

  setCountryFormat (country) {
    if (country === 'US') {
      this._isUsFormat = true
    } else if (country !== 'UK') {
      this._isDutchFormat = true
    }
  }

  getTimezone () {
    return this._timezone
  }

  getTimezoneOffset () {
    return this._offset
  }

  getDefaultTimezone () {
    let defaultZone = ''

    try {
      const timezone = jstz.determine()
      defaultZone = timezone.name()
    } catch (_e) {
      // Left blank intentionally
    }

    return defaultZone
  }

  getTimezoneOptions () {
    const timezoneList = require('./timezones.json')
    const options = []

    timezoneList.map(timezoneInfo => {
      if (timezoneInfo.utc && timezoneInfo.utc.length > 0) {
        timezoneInfo.utc.map(utcItem => {
          options.push({ label: utcItem, value: utcItem })
        })
      }
    })

    return options
  }

  getTimezoneInfo (timezone) {
    const timezoneList = require('./timezones.json')
    let info = null

    timezoneList.map(timezoneInfo => {
      if (
        timezoneInfo.utc &&
        timezoneInfo.utc.length > 0 &&
        _.findIndex(timezoneInfo.utc, utcItem => timezone === utcItem) !== -1
      ) {
        info = timezoneInfo
      }
    })

    return info
  }

  convert (utcDate) {
    /* FOR DEMO */
    return utcDate ? new Date(utcDate) : null

    /* if (!utcDate) return null;

    utcDate = new Date(utcDate);
    return new Date(utcDate.getTime() + (this._offset * 60 * 60 * 1000)); */
  }

  isDateEqual (date1, date2) {
    if (!date1 || !date2) return false

    date1 = new Date(date1)
    date2 = new Date(date2)

    if (
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear() &&
      date1.getDate() === date2.getDate()
    ) {
      return true
    }

    return false
  }

  addDaysToDate (date, incrDays) {
    date = date || this.getUTCCurrentDate()

    const newDate = new Date(date)
    newDate.setDate(newDate.getDate() + parseFloat(incrDays))

    return newDate
  }

  getMondayByDate (date) {
    const newDate = new Date(date)
    if (newDate.getDay() === 0) {
      newDate.setDate(newDate.getDate() - 1)
    }

    return moment(newDate)
      .startOf('week')
      .add(1, 'day')
      .startOf('day')
      .toDate()
  }

  /* display dates  - will convert and show it */
  getDisplayDate (date) {
    const resDate = date ? this.convert(date) : null
    if (resDate) {
      if (this._isDutchFormat) {
        return `${resDate.getDate()}-${resDate.getMonth() +
          1}-${resDate.getFullYear()}`
      } else if (this._isUsFormat) {
        const yearFormat = parseInt(
          resDate
            .getFullYear()
            .toString()
            .substr(-2)
        )
        return `${resDate.getMonth() + 1}/${resDate.getDate()}/${yearFormat}`
      }

      return `${resDate.getDate()}/${resDate.getMonth() +
        1}/${resDate.getFullYear()}`
    }

    return ''
  }

  getDisplayPickerDate (date) {
    const resDate = date ? this.convert(date) : null
    if (resDate) {
      const day = `0${resDate.getDate()}`.substr(-2)
      const month = `0${resDate.getMonth() + 1}`.substr(-2)
      if (this._isDutchFormat) {
        return `${day}-${month}-${resDate.getFullYear()}`
      } else if (this._isUsFormat) {
        const yearFormat = parseInt(
          resDate
            .getFullYear()
            .toString()
            .substr(-2)
        )
        return `${month}/${day}/${yearFormat}`
      }

      return `${day}/${month}/${resDate.getFullYear()}`
    }

    return ''
  }

  getDisplayInputFormat () {
    if (this._isDutchFormat) {
      return '99-99-9999'
    } else if (this._isUsFormat) {
      return '99/99/99'
    }

    return '99/99/9999'
  }

  getDateFormat () {
    if (this._isDutchFormat) {
      return 'DD-MM-YYYY'
    } else if (this._isUsFormat) {
      return 'MM/DD/YY'
    }

    return 'DD/MM/YYYY'
  }

  getDisplayDateMonth (date) {
    const resDate = date ? this.convert(date) : null
    if (resDate) {
      if (this._isUsFormat) {
        return `${('0' + (resDate.getMonth() + 1)).slice(-2)}/${(
          '0' + resDate.getDate()
        ).slice(-2)}`
      }

      return `${('0' + resDate.getDate()).slice(-2)}/${(
        '0' +
        (resDate.getMonth() + 1)
      ).slice(-2)}`
    }

    return ''
  }

  getDisplayIsWeekEnd (date) {
    date = date ? this.convert(date) : null
    return date.getDay() === 0 || date.getDay() === 6
  }

  getDisplayMonthNumber (date) {
    date = date ? this.convert(date) : null
    return date.getMonth() + 1
  }

  getDisplayTime (value, defaultVal) {
    const resDate = value ? this.convert(value) : null
    if (resDate) {
      let hours = resDate.getHours()
      hours = hours > 9 ? hours : `${hours}`
      let mins = resDate.getMinutes()
      mins = mins > 9 ? mins : `0${mins}`
      return `${hours}:${mins}`
    }

    return defaultVal || ''
  }

  getDisplayMonth (date) {
    date = date ? this.convert(date) : null
    return this.getDisplayMonthByNumber(date.getMonth())
  }

  getDisplayMonthByNumber (monthNo) {
    const shortMonth = [
      'common.list.monthJan',
      'common.list.monthFeb',
      'common.list.monthMar',
      'common.list.monthApr',
      'common.list.monthMay',
      'common.list.monthJun',
      'common.list.monthJul',
      'common.list.monthAug',
      'common.list.monthSep',
      'common.list.monthOct',
      'common.list.monthNov',
      'common.list.monthDec'
    ]
    return shortMonth[monthNo]
      ? IntlUtil.convertMessage(shortMonth[monthNo])
      : ''
  }

  getDisplayFullMonth (date) {
    date = date ? this.convert(date) : null
    return this.getDisplayFullMonthByNumber(date.getMonth())
  }

  getDisplayFullMonthByNumber (monthNo) {
    const months = [
      'planner.planner.monthJanuary',
      'planner.planner.monthFebruary',
      'planner.planner.monthMarch',
      'planner.planner.monthApril',
      'planner.planner.monthMay',
      'planner.planner.monthJune',
      'planner.planner.monthJuly',
      'planner.planner.monthAugust',
      'planner.planner.monthSeptember',
      'planner.planner.monthOctober',
      'planner.planner.monthNovember',
      'planner.planner.monthDecember'
    ]

    return IntlUtil.convertMessage(months[monthNo])
  }

  getDisplayWeek (date) {
    date = date ? this.convert(date) : null
    return this._getWeek(date)
  }

  getDisplayWeekDay (date) {
    date = date ? this.convert(date) : null
    const days = [
      'time.header.daySunday',
      'time.header.dayMonday',
      'time.header.dayTuesday',
      'time.header.dayWednesday',
      'time.header.dayThursday',
      'time.header.dayFriday',
      'time.header.daySaturday'
    ]

    return IntlUtil.convertMessage(days[date.getDay()])
  }

  getDisplayWeekDayShort (date) {
    date = date ? this.convert(date) : null
    const days = [
      'time.header.dayShortSunday',
      'time.header.dayShortMonday',
      'time.header.dayShortTuesday',
      'time.header.dayShortWednesday',
      'time.header.dayShortThursday',
      'time.header.dayShortFriday',
      'time.header.dayShortSaturday'
    ]

    return IntlUtil.convertMessage(days[date.getDay()])
  }

  getDisplayDayTitle (day) {
    const days = [
      'time.header.dayMonday',
      'time.header.dayTuesday',
      'time.header.dayWednesday',
      'time.header.dayThursday',
      'time.header.dayFriday',
      'time.header.daySaturday',
      'time.header.daySunday'
    ]

    return IntlUtil.convertMessage(days[day - 1])
  }

  getDisplayDay (date) {
    date = date ? this.convert(date) : null
    return date.getDate()
  }

  getDisplayYear (date) {
    date = date ? this.convert(date) : null
    return date.getFullYear()
  }

  /**
   * returns timeDiff humanized, ex: 3 years and 1 month
   * @param {*} timeDiff in milliseconds
   */
  getHumanizedDuration (timeDiff) {
    let lang = IntlUtil.getLang()

    // Throughout the app the language for the US is set to 'us' instead of 'en'
    // which is not a valid language this should be cleaned up in a later ticket
    // the package humanize-duration(https://www.npmjs.com/package/humanize-duration)
    // also doesnt support 'us' as a language, this should be 'en'
    // This is a temprary fix for this issue and will be fixed in RPD-321
    if (lang === 'us') lang = 'en'

    const options = {
      units: ['y', 'mo', 'd'],
      round: true,
      conjunction: ` ${IntlUtil.convertMessage('common.list.andSplitter')} `,
      language: lang
    }
    return humanizeDuration(timeDiff, options)
  }

  /**
   * returns dueDate humanized with status compaired with today: late, left, and due today
   * @param {*} dueDate standard Date
   * @returns humanizedDate 'late' if dueDate<today
   *          humanizedDate 'left' if dueDate>today
   *          'Due Today' if dueDate===today
   */
  displayHumanized (dueDate) {
    if (!dueDate) {
      return ''
    }

    const currDate = moment()
      .startOf('day')
      .toDate()
    const dueDateObj = moment(dueDate)
      .startOf('day')
      .toDate()
    const diff = moment(dueDateObj).diff(currDate)

    // diff<0: means the dueDate<today
    if (moment.duration(diff < 0 ? -diff : diff).asHours() < 24) {
      return `${IntlUtil.convertMessage('common.list.timeAgoLabelDueToday')}`
    } else if (diff < 0) {
      return `${this.getHumanizedDuration(diff)}  ${IntlUtil.convertMessage(
        'common.list.labelDateLate'
      )}`
    } else {
      return `${this.getHumanizedDuration(diff)}  ${IntlUtil.convertMessage(
        'common.list.labelDateLeft'
      )}`
    }
  }

  getDisplayDayNoOfWeek (date) {
    date = this.convert(date)
    return date.getDay()
  }

  getDisplayDayMonth (date) {
    const resDate = date ? this.convert(date) : null
    if (this._isUsFormat) {
      return `${this.getDisplayMonth(resDate)} ${this.getDisplayDay(resDate)} `
    }

    return `${this.getDisplayDay(resDate)} ${this.getDisplayMonth(resDate)}`
  }

  getDisplayDateWithDay (date) {
    const resDate = date ? this.convert(date) : null
    if (this._isUsFormat) {
      return `${this.getDisplayWeekDayShort(resDate)} ${this.getDisplayDayMonth(
        resDate
      )} ${this.getDisplayYear(resDate)}`
    }

    return `${this.getDisplayWeekDayShort(resDate)} ${this.getDisplayDayMonth(
      resDate
    )} ${this.getDisplayYear(resDate)}`
  }

  /** for timezone */
  getLocalDayNo () {
    const currDateLocal = this.getLocalDate()
    return currDateLocal.getDate()
  }

  getLocalWeekNo (date) {
    const currDateLocal = date || this.getLocalDate()
    return this._getWeek(currDateLocal)
  }

  getLocalYear () {
    const currDateLocal = this.getLocalDate()
    return currDateLocal.getFullYear()
  }

  getLocalMonth () {
    const currDateLocal = this.getLocalDate()
    return currDateLocal.getMonth()
  }

  getLocalTimezoneOffset () {
    return new Date().getTimezoneOffset()
  }

  getLocalDate () {
    const currDateUTC = this.getUTCCurrentDate()
    return this.convert(currDateUTC)
  }

  /** form dates - all dates manipulated in utc date and return only utc */
  convertDatetoUTCStartDate (date) {
    return moment(date)
      .utc()
      .hour(0)
      .minute(0)
      .second(0)
      .toDate()
  }

  getUTCDateFromDate (date) {
    return this._getUTCDate(new Date(date))
  }

  getUTCDateFromTime (time) {
    return this._getUTCDate(new Date(time))
  }

  getUTCDate (year, month, day, hour, min, sec) {
    return this._getUTCDate(
      new Date(year, month, day, hour || 0, min || 0, sec || 0)
    )
  }

  fixDateUTCOffset (date) {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  }

  getUTCCurrentDate () {
    return this._getUTCDate(new Date())
  }

  getDayNames () {
    let dayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

    if (IntlUtil.getLang() === 'nl') {
      dayNames = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo']
    } else if (IntlUtil.getLang() === 'de') {
      dayNames = ['Ma', 'Di', 'Mi', 'Do', 'Vr', 'Sa', 'So']
    }

    return dayNames
  }

  /* Private functions */
  _getUTCDate (date) {
    /* FOR DEMO */
    return date

    /* if (!date) return null;

    date = new Date(date);
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000); */
  }

  _getWeek (date) {
    return moment(date).isoWeek()
  }
}

export default new DateUtil()
